/**
 * Card-related utility functions for poker game handling
 */

export type CardPosition = 'card1' | 'card2' | 'flop1' | 'flop2' | 'flop3' | 'turn' | 'river';

export interface HeroCards {
  holeCard1: string;
  holeCard2: string;
}

export interface CommunityCards {
  flopCard1: string;
  flopCard2: string;
  flopCard3: string;
  turnCard: string;
  riverCard: string;
}

/**
 * Maps card picker position to community card property names
 */
export const getCardPropertyMapping = (): Record<string, string> => {
  return {
    'flop1': 'flopCard1',
    'flop2': 'flopCard2', 
    'flop3': 'flopCard3',
    'turn': 'turnCard',
    'river': 'riverCard'
  };
};

/**
 * Gets all currently used cards to prevent duplicates
 */
export const getUsedCards = (
  heroCards: HeroCards,
  communityCards: CommunityCards,
  currentPosition: CardPosition
): string[] => {
  const used: string[] = [];
  
  // Add hero cards (exclude current position to allow re-selection)
  if (heroCards.holeCard1 && currentPosition !== 'card1') {
    used.push(heroCards.holeCard1);
  }
  if (heroCards.holeCard2 && currentPosition !== 'card2') {
    used.push(heroCards.holeCard2);
  }
  
  // Add community cards (exclude current position to allow re-selection)
  if (communityCards.flopCard1 && currentPosition !== 'flop1') {
    used.push(communityCards.flopCard1);
  }
  if (communityCards.flopCard2 && currentPosition !== 'flop2') {
    used.push(communityCards.flopCard2);
  }
  if (communityCards.flopCard3 && currentPosition !== 'flop3') {
    used.push(communityCards.flopCard3);
  }
  if (communityCards.turnCard && currentPosition !== 'turn') {
    used.push(communityCards.turnCard);
  }
  if (communityCards.riverCard && currentPosition !== 'river') {
    used.push(communityCards.riverCard);
  }
  
  return used;
};

/**
 * Determines if we should auto-transition to the next card selection
 */
export const shouldAutoTransition = (
  position: CardPosition,
  heroCards: HeroCards,
  communityCards: CommunityCards
): { shouldTransition: boolean; nextPosition?: CardPosition } => {
  switch (position) {
    case 'card1':
      // Auto-transition to card2 if it's empty
      return {
        shouldTransition: !heroCards.holeCard2,
        nextPosition: 'card2'
      };
    
    case 'flop1':
      // Auto-transition to flop2 if it's empty
      return {
        shouldTransition: !communityCards.flopCard2,
        nextPosition: 'flop2'
      };
    
    case 'flop2':
      // Auto-transition to flop3 if it's empty
      return {
        shouldTransition: !communityCards.flopCard3,
        nextPosition: 'flop3'
      };
    
    default:
      // No auto-transition for card2, flop3, turn, river
      return { shouldTransition: false };
  }
};

/**
 * Updates hero cards with a new card selection
 */
export const updateHeroCards = (
  currentCards: HeroCards,
  position: CardPosition,
  newCard: string
): HeroCards => {
  if (position === 'card1') {
    return {
      ...currentCards,
      holeCard1: newCard
    };
  } else if (position === 'card2') {
    return {
      ...currentCards,
      holeCard2: newCard
    };
  }
  
  // Return unchanged if position is not a hero card position
  return currentCards;
};

/**
 * Updates community cards with a new card selection
 */
export const updateCommunityCards = (
  currentCards: CommunityCards,
  position: CardPosition,
  newCard: string
): CommunityCards => {
  const propertyMapping = getCardPropertyMapping();
  const propertyName = propertyMapping[position];
  
  if (!propertyName) {
    // Return unchanged if position is not a community card position
    return currentCards;
  }
  
  return {
    ...currentCards,
    [propertyName]: newCard
  };
};

/**
 * Checks if a card position is for hero cards
 */
export const isHeroCardPosition = (position: CardPosition): boolean => {
  return position === 'card1' || position === 'card2';
};

/**
 * Checks if a card position is for community cards
 */
export const isCommunityCardPosition = (position: CardPosition): boolean => {
  return ['flop1', 'flop2', 'flop3', 'turn', 'river'].includes(position);
};

/**
 * Gets the display name for a card position
 */
export const getCardPositionDisplayName = (position: CardPosition): string => {
  const displayNames: Record<CardPosition, string> = {
    'card1': 'Hero Card 1',
    'card2': 'Hero Card 2', 
    'flop1': 'Flop Card 1',
    'flop2': 'Flop Card 2',
    'flop3': 'Flop Card 3',
    'turn': 'Turn Card',
    'river': 'River Card'
  };
  
  return displayNames[position] || position;
};